<template>
  <div
    id="main-slide"
    ref="slide-container"
    class="main-slide"
    :class="{
      'has-share-presentation': sharePresentation,
      'border-base-30 border box-content rounded-md': !isPresenting,
    }"
  >
    <div
      id="slide-container"
      class="slide-container"
      :class="{
        'is-showing-qrcode-panel': isShowingQRCodePanel
      }"
    >
      <transition
        name="qr-code-panel"
        enter-active-class="animate__animated animate__slideInLeft animate__faster"
        leave-active-class="animate__animated animate__slideOutLeft animate__faster"
      >
        <QRcodePanel v-if="$store.state.isQRCodeVisible && isPresentingOrBackstage" />
      </transition>

      <div
        class="content-presenting"
        :class="{
          'show-control': isShowingControls,
          'is-showing-qrcode-panel': isShowingQRCodePanel
        }"
        :style="{'color': slideActive.textColour }"
      >
        <CanvasTransitionBackgroundImage />

        <CanvasChooseASlideType v-if="isChooseASlideTypeVisible" />
        <Slide />
        <CanvasLeaderboardMusic />
        <div
          v-if="!hideOnSharingViewForPureContent"
          :class="{'canvas-pill': !isScreenCapture,
                   'white':isTextColourLight,
                   'no-background': !isPresenting}"
        >
          <CanvasArrowsNavigation
            @selectPrevSlide="selectPrevSlide"
            @selectNextSlide="selectNextSlide"
          />
        </div>

        <div v-if="$store.state.showQnaSession" class="show-qna-session">
          <div class="box-qna-session">
            <QaSession />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Hammer from 'hammerjs'
import EventBus from '@/eventBus'
import utils from '@/util'
import configs from '@/config'
import constants from '@/constant'
import Socket from '@/socket'
import ToggleQRCodeMixin from '@/mixins/presentation/ToggleQRCodeMixin'

// components
import CanvasChooseASlideType from '@/components-v2/common/canvas-preview/CanvasChooseASlideType.vue'
import Slide from '@/components/presentations/Slide.vue'
import CanvasTransitionBackgroundImage from '@/components-v2/presentation-editor/middle/CanvasTransitionBackgroundImage.vue'
import QRcodePanel from '@/components-v2/presentation-editor/middle/QRcodePanel.vue'
import CanvasLeaderboardMusic from '@/components-v2/presentation-editor/middle/CanvasLeaderboardMusic.vue'
import QaSession from '@/components/presentations/QaSession.vue'
import CanvasArrowsNavigation from '@/components-v2/presentation-editor/middle/CanvasArrowsNavigation.vue'

const tracker = utils
  .gaTrackEventFactory({
    eventCategory: configs.US_PRESENTING_MODE,
    eventLabel: configs.LABEL_USER_INTERACTION,
  })
  .bind(utils)

export default {
  components: {
    Slide,
    CanvasTransitionBackgroundImage,
    CanvasLeaderboardMusic,
    CanvasChooseASlideType,
    QaSession,
    QRcodePanel,
    CanvasArrowsNavigation,
  },
  mixins: [ToggleQRCodeMixin],
  data() {
    return {
      showContextMenu: false,
      isShowingControls: false,

      targetClick: null,
      isMSTeamsApp: !!utils.getParamValue('ms-teams', window.location.href),

    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      isQRCodeVisible: (state) => state.isQRCodeVisible,
      isScreenCapture: (state) => state.isScreenCapture,
    }),
    ...mapGetters('presentationChat', ['isFocusingChatInput']),
    ...mapGetters(['isPresenting', 'isBackstage', 'isTextColourLight']),

    isQRCodeSlide() {
      const { type } = this.slideActive
      return type === 'qrcode' && this.$store.state.presentation.slides.some((slide) => slide.type === 'pickAnswer')
    },

    hasPrevSlide() {
      return this.$store.getters.currentSlideIndex > 0
    },
    hasNextSlide() {
      return (
        this.$store.getters.currentSlideIndex <
        this.$store.state.presentation.slides.length - 1
      )
    },
    isShowingQRCodePanel() {
      return this.isPresenting && this.isQRCodeVisible
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
    isChooseASlideTypeVisible() {
      return this.isNewPresentation || !this.slideActive.type
    },
    hideOnSharingViewForPureContent() {
      return !!utils.getParamValue('pureContent', window.location.href) && this.sharePresentation
    },
  },
  watch: {
    isPresenting() {
      if (!this.isPresenting) this.showContextMenu = false
    },
  },
  mounted() {
    this.handleSwipeEvent()

    EventBus.$on('context-menu: update showContextMenu', (value) => {
      this.showContextMenu = value
    })
    EventBus.$on('next-back-slide-arrows: update isShowingControls', (value) => {
      this.isShowingControls = value
    })
    EventBus.$on('onKeyboard', (e) => {
      this.handleSlideControlsKeydown(e)
    })
    EventBus.$on('eventHandleNextSlide', (type) => {
      this.$emit('handleNextSlideWhenPlayingQuiz', type)
    })
  },
  destroyed() {
    EventBus.$off('context-menu: update showContextMenu')
    EventBus.$off('next-back-slide-arrows: update isShowingControls')
    EventBus.$off('onKeyboard')
    EventBus.$off('eventHandleNextSlide')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setCurrentUserAction']),
    handleSwipeEvent() {
      if (!!utils.getParamValue('swipeControl', window.location.href) && this.sharePresentation) {
        this._handleSwipeEventForSharingViewOnAudience()

        return
      }

      const hammertime = new Hammer(this.$refs['slide-container'])
      hammertime.on('swipe', (ev) => {
        if (!this.isPresenting && !this.isBackstage) return
        if (ev.direction === Hammer.DIRECTION_RIGHT) {
          if (this.hasPrevSlide) this.selectPrevSlide('swipe')
        } else {
          tracker({ eventAction: 'User swiped to next slide' })
          if (this.hasNextSlide) this.selectNextSlide('swipe')
        }
      })
    },

    _handleSwipeEventForSharingViewOnAudience() {
      const hammertime = new Hammer(this.$refs['slide-container'])

      hammertime.on('swipe', (ev) => {
        if (ev.direction === Hammer.DIRECTION_RIGHT) {
          window.parent.postMessage({ direction: 'right' }, '*')
        }

        if (ev.direction === Hammer.DIRECTION_LEFT) {
          window.parent.postMessage({ direction: 'left' }, '*')
        }
      })
    },

    selectPrevSlide(mode) {
      this.$emit('selectPrevSlide', mode)
    },
    selectNextSlide(mode) {
      this.$emit('selectNextSlide', mode)
    },
    isFocusingInput() {
      return this.isFocusingChatInput || this.slideActive.isFocusingSlideNoteInput
    },

    handleSlideControlsKeydown(e) {
      if (!this.isPresenting && !this.isBackstage) return

      // If popup in visible in leaderboard, do nothing
      if (this.slideActive.type === 'leaderboard'
       && this.slideActive.visiblePopup) return
      // If chatbox input is focusing, and the key is not Esc, then do nothing
      if (this.isFocusingInput() && !configs.EXIT_KEYS.includes(e.code)) return

      if (this.slideActive.type == 'pickAnswer' && this.$store.state.quizConfirmation && !this.sharePresentation) {
        if (this.$store.state.historyKeyboard == e.code) {
          this.$emit('handleNextSlideWhenPlayingQuiz', this.$store.state.quizConfirmationType)
        } else {
          this.$store.state.quizConfirmation = null
        }
        return
      }

      this.$store.state.historyKeyboard = e.code
      this._handleControlKeydown(e)
      if (this.slideActive.type === 'bulletPoints') this._handleNextBackBulletPointsIndex(e)
    },

    _handleControlKeydown(e) {
      const arrPreventKeys = [
        'Enter',
        'Space',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ]
      if (
        this.sharePresentation &&
        this.$store.state.isFocusInputCommentPresentation &&
        arrPreventKeys.includes(e.code)) {
        return
      }
      if (configs.NEXT_SLIDE_KEYS.includes(e.code)) {
        if (
          this.slideActive.type == 'pickAnswer' &&
          this.slideActive.quizStatus == constants.QuizStatuses.LOBBY_SCREEN &&
          !this.sharePresentation
        ) {
          EventBus.$emit('start-quiz')
          return
        }
        this.selectNextSlide('keyboard')
      }
      if (configs.LAST_SLIDE_KEYS.includes(e.code)) {
        this.selectPrevSlide('keyboard')
      }
      if (configs.EXIT_KEYS.includes(e.code) && this.isQRCodeVisible) {
        this.handleToggleQrCode()
      }
      if (e.code === 'Enter') {
        EventBus.$emit('slide-handle-enter')
      }
    },

    handleToggleQrCode() {
      this.toggleQrCode('PressEsc') // ToggleQRCodeMixin
    },

    _handleNextBackBulletPointsIndex(e) {
      if (e.key == 'ArrowDown') {
        if (
          this.slideActive.SlideOptions.length <=
            this.slideActive.bulletPointsIndex
        ) return
        EventBus.$emit('onKeyboard', {
          code: 'ArrowRight',
        })
      } else if (e.key == 'ArrowUp') {
        if (
          !this.slideActive.bulletPointsIndex ||
            this.slideActive.bulletPointsIndex < 1
        ) {
          return
        }
        EventBus.$emit('onKeyboard', {
          code: 'ArrowLeft',
        })
      }
    },

    async _updateSlide(params) {
      params.socketId = Socket.getSocketId()
      params.presenting = this.isPresenting
      await this.$http.put(configs.api.updateSlide + this.slideActive.id, params)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.slide-container {
  transform-origin: top left;
  overflow: hidden;
  background-color: #fff;
  transform: translateZ(0);
  -webkit-font-smoothing: subpixel-antialiased;
  backface-visibility: hidden;
  -webkit-box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  -moz-box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  transition: background-color 0.3s ease;
  &.is-showing-qrcode-panel {
    background-color: #000;
  }
}

.white-icon {
  filter: invert(1);
}
.backdrop {
  position: fixed;
  width: 80%;
  height: 100%;
  z-index: 999;
  top: 0;
  left: 21%;
  &.is-showing-qr-code-panel {
    transform: translate(20%);
  }
}
.content-presenting {
  position: relative;
  height: 100%;
  transition: transform 0.5s ease;

  &.show-control {
    &::v-deep .slide-control {
      opacity: 1;
    }
    &::v-deep {
      .quiz-music-toggle, .escape-presenting, .private-mode, .freestyle-audio-toggle-container {
        opacity: 1;
      }
    }
  }
  &.is-showing-qrcode-panel {
    transform: translateX(10%) scale(0.8);
    overflow: hidden;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
}
.canvas-pill {
  font-family: Nunito;
  position: relative;
  bottom: 4rem;
  height: 43px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin-left: 1rem;
  padding-left : 0.5rem;
  padding-right: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 27px;
  transition: 0.3s;
  margin-top: 2px;
  &.white {
    background: rgba(0, 0, 0, 0.5);

    .hover-item::after{
      background: rgba(211, 215, 225, 0.2);
    }
  }

  &.no-background {
    background: transparent;
    backdrop-filter: unset;
  }

  .is-qrcode-visible {
    z-index: 19;
  }
}

.show-qna-session {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 25;
  padding: 64px;

  .box-qna-session {
    width: 100%;
    height: 100%;
    border-radius: 4px;
  }

  @media (max-width: 768px) and (orientation: landscape) {
    padding-top: 86px;
    padding-bottom: 42px;
  }
}
</style>
